import { useQuery, UseQueryOptions } from 'react-query';
import client from 'client';
import { ScheduledMeeting } from '../types';
import { parseScheduleMeeting } from './parseScheduleMeeting';

type MapperResponse = {
    content: ScheduledMeeting[];
    page: Page;
};

export interface ScheduledMeetingResponse
    extends Omit<ScheduledMeeting, 'startDate' | 'endDate' | 'times' | 'created' | 'modified'> {
    startDate?: string;
    endDate?: string;
    times?: string[];
    created?: string;
    modified?: string;
}

type Page = {
    number: number;
    size: number;
    totalElements: number;
    totalPages: number;
};

type ScheduledMeetingsDataResponse = {
    content: ScheduledMeetingResponse[];
    page: Page;
};

const fetchMeetings = async ({
    eventId,
    size,
    page,
}: {
    eventId: number;
    size: number;
    page: number;
}) => {
    const response = await client().get<ScheduledMeetingsDataResponse>(
        `/api/v2/meetings/events/${eventId}/scheduledMeetings?size=${size}&page=${page}`
    );
    return response.data;
};

const mapper = (response: ScheduledMeetingsDataResponse): MapperResponse => {
    const content = response.content.map(parseScheduleMeeting);
    return {
        ...response,
        content,
    };
};

const useScheduledMeetings = ({
    key,
    eventId,
    size,
    page,
    options,
}: {
    key: string;
    eventId: number;
    size: number;
    page: number;
    options?: UseQueryOptions<ScheduledMeetingsDataResponse, Error, MapperResponse>;
}) => {
    return useQuery(key, () => fetchMeetings({ eventId, size, page }), {
        staleTime: 0,
        refetchOnWindowFocus: false,
        select: mapper,
        ...options,
        useErrorBoundary: true,
    });
};

export default useScheduledMeetings;
